import { useEffect, useMemo, useRef, useState } from 'react';
import { AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import { CriteoSponsoredProduct } from '@jbhi-fi/apis/src/criteo/types';
import { productClient } from '../../api/algolia/ProductClient';
import { useFeatureFlag } from '../useFeatureFlag/useFeatureFlag';
import {
  DEFAULT_SPONSORED_VARIATION_TYPE,
  MergeCriteoWithAlgoliaHitsResult,
  getMissingCriteoProductKeys,
  isSponsoredVariationType,
  mergeCriteoWithAlgoliaHits,
} from '../../utils/algolia/mergeCriteoWithAlgoliaHits';

type UseSponsoredProductsArgs = {
  isEnabled: boolean;
  isEnabledResolved?: boolean;
  requestKey: string;
  fetchSponsoredProducts: () => Promise<Array<CriteoSponsoredProduct>>;
};

type UseSponsoredProductsReturn = {
  sponsoredProducts: Array<CriteoSponsoredProduct>;
  isLoading: boolean;
};

type UseSponsoredDisplayHitsArgs = {
  organicHits: Array<AlgoliaProductHit>;
  sponsoredProducts?: Array<CriteoSponsoredProduct>;
  isSponsoredProductsLoading?: boolean;
  itemsPerRow?: number | null;
  sponsoredVariationType?: Parameters<typeof mergeCriteoWithAlgoliaHits>[0]['sponsoredVariationType'];
  contextKey: string;
  visibleCount?: number;
};

type UseSponsoredDisplayHitsReturn = {
  displayHits: Array<AlgoliaProductHit>;
  mergedResults: MergeCriteoWithAlgoliaHitsResult;
  isReady: boolean;
};

type UseItemsPerRowMeasurementArgs = {
  isEnabled?: boolean;
  gridSelector?: string;
  itemSelector?: string;
  measurementKey?: string;
};

type GetItemsPerRowArgs = {
  container: HTMLElement | null;
  gridSelector?: string;
  itemSelector?: string;
  tolerance?: number;
};

const getSupplementalSponsoredHitKey = (hit: AlgoliaProductHit): string =>
  [hit.objectID, hit.sku || hit.product?.id].filter(Boolean).join(':') || String(hit.sku || hit.product?.id || '');
const EMPTY_SPONSORED_PRODUCTS: Array<CriteoSponsoredProduct> = [];

const getUnattemptedMissingSponsoredProductKeys = ({
  algoliaHits,
  criteoProducts,
  attemptedSponsoredKeys,
}: {
  algoliaHits: Array<AlgoliaProductHit>;
  criteoProducts: Array<CriteoSponsoredProduct>;
  attemptedSponsoredKeys: Set<string>;
}): Array<string> =>
  getMissingCriteoProductKeys({
    algoliaHits,
    criteoProducts,
  }).filter(key => !attemptedSponsoredKeys.has(key));

const countTopLevelTracks = (gridTemplateColumns: string): number => {
  let count = 0;
  let depth = 0;
  let token = '';

  for (const character of gridTemplateColumns.trim()) {
    if (character === '(') {
      depth += 1;
    }

    if (character === ')') {
      depth = Math.max(0, depth - 1);
    }

    if (/\s/.test(character) && depth === 0) {
      if (token) {
        count += 1;
        token = '';
      }

      continue;
    }

    token += character;
  }

  if (token) {
    count += 1;
  }

  return count;
};

const getLayoutRoot = ({
  container,
  gridSelector,
}: Pick<GetItemsPerRowArgs, 'container' | 'gridSelector'>): HTMLElement | null => {
  if (!container) {
    return null;
  }

  return (gridSelector ? container.querySelector<HTMLElement>(gridSelector) : container) ?? container;
};

const getVisibleElements = (layoutRoot: HTMLElement, itemSelector: string): Array<HTMLElement> =>
  Array.from(layoutRoot.querySelectorAll<HTMLElement>(itemSelector)).filter(
    element => element instanceof HTMLElement && element.offsetParent !== null
  );

const getGridColumnCount = (layoutRootStyle: CSSStyleDeclaration): number | null => {
  if (!layoutRootStyle.display.includes('grid') || layoutRootStyle.gridTemplateColumns === 'none') {
    return null;
  }

  const gridColumnCount = countTopLevelTracks(layoutRootStyle.gridTemplateColumns);

  return gridColumnCount > 0 ? gridColumnCount : null;
};

const countFlexItemsPerRow = (elements: Array<HTMLElement>, tolerance: number): number | null => {
  if (!elements.length) {
    return null;
  }

  const firstLeft = elements[0].getBoundingClientRect().left;
  let count = 1;

  for (const element of elements.slice(1)) {
    const left = element.getBoundingClientRect().left;

    if (left <= firstLeft + tolerance) {
      break;
    }

    count += 1;
  }

  return count;
};

const countItemsInFirstRenderedRow = (elements: Array<HTMLElement>, tolerance: number): number | null => {
  if (!elements.length) {
    return null;
  }

  const firstRowTop = elements[0].getBoundingClientRect().top;
  let count = 0;

  for (const element of elements) {
    if (Math.abs(element.getBoundingClientRect().top - firstRowTop) > tolerance) {
      break;
    }

    count += 1;
  }

  return count || null;
};

const getItemsPerRow = ({
  container,
  gridSelector,
  itemSelector = '.product-tile__container',
  tolerance = 1,
}: GetItemsPerRowArgs): number | null => {
  const layoutRoot = getLayoutRoot({ container, gridSelector });

  if (!layoutRoot) {
    return null;
  }

  const layoutRootStyle = window.getComputedStyle(layoutRoot);
  const gridColumnCount = getGridColumnCount(layoutRootStyle);

  if (gridColumnCount) {
    return gridColumnCount;
  }

  const visibleElements = getVisibleElements(layoutRoot, itemSelector);

  if (layoutRootStyle.display.includes('flex')) {
    const flexItemsPerRow = countFlexItemsPerRow(visibleElements, tolerance);

    if (flexItemsPerRow) {
      return flexItemsPerRow;
    }
  }

  return countItemsInFirstRenderedRow(visibleElements, tolerance);
};

export const useSponsoredProducts = ({
  isEnabled,
  isEnabledResolved = true,
  requestKey,
  fetchSponsoredProducts,
}: UseSponsoredProductsArgs): UseSponsoredProductsReturn => {
  const [sponsoredProducts, setSponsoredProducts] = useState<Array<CriteoSponsoredProduct>>([]);
  const [completedLoadKey, setCompletedLoadKey] = useState('');
  const hasRequestKey = !!requestKey.trim();
  const shouldLoadSponsoredProducts = isEnabled && hasRequestKey;
  const loadStateKey = shouldLoadSponsoredProducts ? `enabled:${requestKey}` : `disabled:${requestKey}`;
  const isLoading = hasRequestKey && shouldLoadSponsoredProducts && completedLoadKey !== loadStateKey;

  useEffect(() => {
    let isCancelled = false;

    const loadSponsoredProducts = async () => {
      if (!isEnabledResolved) {
        setSponsoredProducts([]);
        setCompletedLoadKey('');
        return;
      }

      if (!shouldLoadSponsoredProducts) {
        setSponsoredProducts([]);
        setCompletedLoadKey(loadStateKey);
        return;
      }

      setSponsoredProducts([]);

      try {
        const nextSponsoredProducts = await fetchSponsoredProducts();

        if (!isCancelled) {
          setSponsoredProducts(nextSponsoredProducts);
          setCompletedLoadKey(loadStateKey);
        }
      } catch {
        if (!isCancelled) {
          setSponsoredProducts([]);
          setCompletedLoadKey(loadStateKey);
        }
      }
    };

    void loadSponsoredProducts();

    return () => {
      isCancelled = true;
    };
  }, [fetchSponsoredProducts, isEnabledResolved, requestKey, shouldLoadSponsoredProducts]);

  return {
    sponsoredProducts,
    isLoading,
  };
};

export const useSponsoredVariationType = () => {
  const variationType = useFeatureFlag('sponsoredVariationType');
  return isSponsoredVariationType(variationType) ? variationType : DEFAULT_SPONSORED_VARIATION_TYPE;
};

export const useItemsPerRowMeasurement = <T extends HTMLElement = HTMLDivElement>({
  isEnabled = true,
  gridSelector,
  itemSelector,
  measurementKey,
}: UseItemsPerRowMeasurementArgs = {}) => {
  const containerRef = useRef<T | null>(null);
  const [itemsPerRow, setItemsPerRow] = useState<number | null>(null);

  useEffect(() => {
    if (!isEnabled) {
      setItemsPerRow(null);
      return;
    }

    let animationFrameId = 0;

    const updateItemsPerRow = () => {
      const nextItemsPerRow = getItemsPerRow({
        container: containerRef.current,
        gridSelector,
        itemSelector,
      });

      if (nextItemsPerRow) {
        setItemsPerRow(nextItemsPerRow);
      }
    };

    const scheduleMeasurement = () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = window.requestAnimationFrame(() => {
        animationFrameId = 0;
        updateItemsPerRow();
      });
    };

    scheduleMeasurement();

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' && containerRef.current ? new ResizeObserver(scheduleMeasurement) : null;
    const shouldUseWindowResizeFallback = !resizeObserver;

    if (resizeObserver && containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    if (shouldUseWindowResizeFallback) {
      window.addEventListener('resize', scheduleMeasurement);
    }

    return () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }

      resizeObserver?.disconnect();
      if (shouldUseWindowResizeFallback) {
        window.removeEventListener('resize', scheduleMeasurement);
      }
    };
  }, [gridSelector, isEnabled, itemSelector, measurementKey]);

  return {
    containerRef,
    itemsPerRow,
  };
};

export const useSponsoredDisplayHits = ({
  organicHits,
  sponsoredProducts,
  isSponsoredProductsLoading = false,
  itemsPerRow,
  sponsoredVariationType,
  contextKey,
  visibleCount = organicHits.length,
}: UseSponsoredDisplayHitsArgs): UseSponsoredDisplayHitsReturn => {
  const [supplementalSponsoredHits, setSupplementalSponsoredHits] = useState<Array<AlgoliaProductHit>>([]);
  const [isFetchingMissingSponsoredHits, setIsFetchingMissingSponsoredHits] = useState(false);
  const [attemptedSponsoredKeys, setAttemptedSponsoredKeys] = useState<Set<string>>(new Set());
  const activeSponsoredProducts = sponsoredProducts?.length ? sponsoredProducts : EMPTY_SPONSORED_PRODUCTS;
  const hasSponsoredProducts = activeSponsoredProducts.length > 0;
  const requestVersionRef = useRef(0);
  const supplementalSponsoredHitsRef = useRef<Array<AlgoliaProductHit>>([]);

  useEffect(() => {
    requestVersionRef.current += 1;
    setAttemptedSponsoredKeys(new Set());
    supplementalSponsoredHitsRef.current = [];
    setSupplementalSponsoredHits([]);
    setIsFetchingMissingSponsoredHits(false);
  }, [contextKey, activeSponsoredProducts]);

  const availableAlgoliaHits = useMemo(
    () => [...organicHits, ...supplementalSponsoredHits],
    [organicHits, supplementalSponsoredHits]
  );
  const actionableMissingProductKeys = !hasSponsoredProducts
    ? []
    : getUnattemptedMissingSponsoredProductKeys({
        algoliaHits: availableAlgoliaHits,
        criteoProducts: activeSponsoredProducts,
        attemptedSponsoredKeys,
      });

  useEffect(() => {
    if (!hasSponsoredProducts) {
      setIsFetchingMissingSponsoredHits(false);
      return;
    }
    const missingProductKeys = getUnattemptedMissingSponsoredProductKeys({
      algoliaHits: [...organicHits, ...supplementalSponsoredHitsRef.current],
      criteoProducts: activeSponsoredProducts,
      attemptedSponsoredKeys,
    });

    if (!missingProductKeys.length) {
      return;
    }

    setAttemptedSponsoredKeys(previousAttemptedSponsoredKeys => {
      const nextAttemptedSponsoredKeys = new Set(previousAttemptedSponsoredKeys);

      missingProductKeys.forEach(key => nextAttemptedSponsoredKeys.add(key));

      return nextAttemptedSponsoredKeys;
    });
    const activeRequestVersion = requestVersionRef.current;
    setIsFetchingMissingSponsoredHits(true);

    const fetchMissingSponsoredHits = async () => {
      const { data, error } = await productClient.getProductsFromSkus(missingProductKeys);

      if (requestVersionRef.current !== activeRequestVersion || error || !data?.products?.length) {
        if (requestVersionRef.current === activeRequestVersion) {
          setIsFetchingMissingSponsoredHits(false);
        }
        return;
      }

      setSupplementalSponsoredHits(prev => {
        const nextHits = new Map(prev.map(hit => [getSupplementalSponsoredHitKey(hit), hit]));

        data.products.forEach(hit => {
          nextHits.set(getSupplementalSponsoredHitKey(hit), hit);
        });

        const nextSupplementalSponsoredHits = Array.from(nextHits.values());

        supplementalSponsoredHitsRef.current = nextSupplementalSponsoredHits;

        return nextSupplementalSponsoredHits;
      });
      setIsFetchingMissingSponsoredHits(false);
    };

    void fetchMissingSponsoredHits();
  }, [activeSponsoredProducts, attemptedSponsoredKeys, contextKey, hasSponsoredProducts, organicHits]);

  const hasValidSponsoredVariationType = isSponsoredVariationType(sponsoredVariationType);
  const mergedResults = useMemo(
    () =>
      mergeCriteoWithAlgoliaHits({
        algoliaHits: availableAlgoliaHits,
        criteoProducts: activeSponsoredProducts,
        itemsPerRow,
        sponsoredVariationType,
      }),
    [activeSponsoredProducts, availableAlgoliaHits, itemsPerRow, sponsoredVariationType]
  );
  const hasValidItemsPerRow = Number.isInteger(Number(itemsPerRow)) && Number(itemsPerRow) > 0;
  const shouldApplySponsoredLayout = !hasSponsoredProducts || (hasValidSponsoredVariationType && hasValidItemsPerRow);
  const isWaitingForItemsPerRow = hasSponsoredProducts && hasValidSponsoredVariationType && !hasValidItemsPerRow;
  const isResolvingMissingSponsoredHits = isFetchingMissingSponsoredHits || actionableMissingProductKeys.length > 0;
  const hasUnresolvedSponsoredProducts =
    hasSponsoredProducts && mergedResults.missingProductKeys.some(key => !attemptedSponsoredKeys.has(key));
  const isReady =
    !isSponsoredProductsLoading &&
    !isResolvingMissingSponsoredHits &&
    !hasUnresolvedSponsoredProducts &&
    !isWaitingForItemsPerRow;

  const displayHits = useMemo(
    () =>
      shouldApplySponsoredLayout
        ? mergedResults.prioritizedHits.slice(0, visibleCount)
        : organicHits.slice(0, visibleCount),
    [mergedResults.prioritizedHits, organicHits, shouldApplySponsoredLayout, visibleCount]
  );

  return {
    displayHits,
    mergedResults,
    isReady,
  };
};
